import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import PageSEO from '../components/PageSEO';

export default function LearnMore() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Subtle background animation for hero (similar to LegalHero but fitted for Learn More)
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let nodes = [], animId;

    function resize() {
      if(canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    }

    function initNodes() {
      nodes = [];
      const count = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2,
          r: Math.random() * 1.5 + 0.5
        });
      }
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 120) {
            const alpha = (1 - dist/120) * 0.15;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(254,171,61,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(254,171,61,0.4)';
        ctx.fill();
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    }

    window.addEventListener('resize', () => { resize(); initNodes(); });
    resize();
    initNodes();
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  const faqs = [
    {
      q: "What is AI search visibility?",
      a: "AI search visibility refers to how accurately and favorably your business is represented when customers use AI-powered tools (like ChatGPT, Perplexity, or AI overviews) to find information, products, or services in your industry."
    },
    {
      q: "What does the free audit check?",
      a: "The free audit analyzes key technical and content signals that AI systems rely on, including crawler accessibility, structured data presence, and baseline brand mentions, helping you identify immediate gaps."
    },
    {
      q: "Which AI systems are analyzed?",
      a: "We monitor signals relevant to a broad range of AI-powered discovery engines, including large language models (LLMs) and search-generative experiences, recognizing that these systems often share underlying data sources and evaluation principles."
    },
    {
      q: "Do I need technical knowledge?",
      a: "No. While we analyze technical signals, our reports are designed to be clear and actionable. We tell you exactly what is working, what isn't, and what to prioritize."
    },
    {
      q: "Does AI Search Advisers guarantee recommendations?",
      a: "No. AI systems are non-deterministic and constantly evolving. We provide data-driven insights and best practices to maximize your visibility potential, but cannot guarantee specific rankings or outputs."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://aisearchadvisers.com/#organization",
        "name": "AI Search Advisers",
        "url": "https://aisearchadvisers.com/",
        "description": "AI Search Advisers helps businesses assess and improve website readiness for AI-driven discovery through website audits, AI visibility insights, crawlability analysis, structured data evaluation, and prioritized recommendations.",
        "email": "info@aisearchadvisers.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://aisearchadvisers.com/#website",
        "url": "https://aisearchadvisers.com/",
        "name": "AI Search Advisers",
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "https://aisearchadvisers.com/learn-more#webpage",
        "url": "https://aisearchadvisers.com/learn-more",
        "name": "How AI Search Visibility Works | AI Search Advisers",
        "description": "Learn how AI search visibility works and how crawlability, content, structured data, and website signals can affect discovery across AI search platforms.",
        "isPartOf": { "@id": "https://aisearchadvisers.com/#website" },
        "about": { "@id": "https://aisearchadvisers.com/#organization" },
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      }
    ]
  };

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-sans text-[#021426] selection:bg-[#FEAB3D]/30 selection:text-[#021426] overflow-x-hidden">
      <PageSEO 
        title="How AI Search Visibility Works | AI Search Advisers"
        description="Learn how AI search visibility works and how crawlability, content, structured data, and website signals can affect discovery across AI search platforms."
        canonical="https://aisearchadvisers.com/learn-more"
        schema={schema}
      />
      <GlobalHeader />

      <main>
        {/* 1. HERO SECTION */}
        <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-32 px-6" style={{ background: 'linear-gradient(180deg, #05192E 0%, #06203A 100%)' }}>
          {/* Subtle Grid SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{ filter: 'blur(1px)', opacity: 0.8 }} viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
            <rect width="1440" height="900" fill="#04152a"/>
            <g stroke="rgba(79,144,166,0.06)" strokeWidth="1">
              <line x1="0" y1="150" x2="1440" y2="150"/>
              <line x1="0" y1="300" x2="1440" y2="300"/>
              <line x1="0" y1="450" x2="1440" y2="450"/>
              <line x1="0" y1="600" x2="1440" y2="600"/>
              <line x1="180" y1="0" x2="180" y2="900"/>
              <line x1="360" y1="0" x2="360" y2="900"/>
              <line x1="540" y1="0" x2="540" y2="900"/>
              <line x1="720" y1="0" x2="720" y2="900"/>
            </g>
          </svg>
          <div className="absolute inset-0 z-1 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 30%, rgba(12,91,148,0.2) 0%, transparent 60%)' }}></div>
          <canvas className="absolute inset-0 pointer-events-none z-2" ref={canvasRef}></canvas>

          <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center">
            <div className="inline-block text-[#FEAB3D] font-bold tracking-[0.15em] uppercase text-xs mb-6 px-4 py-1.5 bg-[#FEAB3D]/10 rounded-full border border-[#FEAB3D]/20 backdrop-blur-sm">
              AI SEARCH VISIBILITY
            </div>
            <h1 className="font-['Fraunces',serif] text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-white leading-[1.15] max-w-4xl">
              Understand how AI systems see and recommend your business
            </h1>
            <p className="text-white/80 text-lg sm:text-xl font-medium mb-10 max-w-2xl leading-relaxed">
              AI Search Advisers helps businesses understand their presence across AI-powered discovery systems and identify exactly what needs improvement.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full sm:w-auto">
              <button onClick={() => navigate('/auth')} className="w-full sm:w-auto px-8 py-3.5 bg-[#FEAB3D] text-[#021426] font-semibold rounded hover:bg-[#FEAB3D]/90 transition-colors shadow-[0_4px_20px_rgba(254,171,61,0.25)]">
                Run My Free Audit
              </button>
              <button onClick={() => navigate('/pricing')} className="w-full sm:w-auto px-8 py-3.5 bg-transparent border border-white/20 text-white font-semibold rounded hover:bg-white/5 transition-colors">
                View Pricing
              </button>
            </div>

            <div className="w-full max-w-4xl relative rounded-xl border border-white/10 bg-white/5 p-2 sm:p-4 shadow-[0_20px_60px_rgba(0,0,0,0.3)] backdrop-blur-md overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-[#05192E] via-transparent to-transparent z-10 pointer-events-none opacity-40"></div>
              <img 
                src="/learn-more-dashboard.png" 
                alt="AI Search Advisers Dashboard Preview" 
                className="w-full h-auto rounded-lg shadow-xl relative z-0 object-cover"
              />
            </div>
          </div>
        </section>

        {/* 2. WHY THIS MATTERS */}
        <section className="py-20 lg:py-28 px-6 bg-white border-b border-[#e5e9ee]">
          <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div>
              <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-6">
                Search is changing.
              </h2>
              <p className="text-lg text-[#4C545B] leading-relaxed mb-6">
                People increasingly discover businesses through AI-assisted search, conversational chatbots, and answer engines. Relying solely on traditional SEO is no longer enough.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-[#4C545B]">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0C5B94]/10 flex items-center justify-center text-[#0C5B94] font-bold text-sm mt-0.5">✓</span>
                  <span>AI models synthesize answers instead of just providing links.</span>
                </li>
                <li className="flex items-start gap-3 text-[#4C545B]">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0C5B94]/10 flex items-center justify-center text-[#0C5B94] font-bold text-sm mt-0.5">✓</span>
                  <span>If an AI cannot properly read your site, you will not be recommended.</span>
                </li>
                <li className="flex items-start gap-3 text-[#4C545B]">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0C5B94]/10 flex items-center justify-center text-[#0C5B94] font-bold text-sm mt-0.5">✓</span>
                  <span>Competitors are already optimizing for AI discovery systems.</span>
                </li>
              </ul>
            </div>
            
            {/* Conceptual Flow Visual */}
            <div className="relative rounded-2xl bg-[#FAF7F2] border border-[#e5e9ee] p-8 sm:p-12 shadow-sm flex flex-col items-center gap-6">
              <div className="bg-white border border-[#e5e9ee] rounded-lg p-4 shadow-sm w-full max-w-xs text-center relative z-10">
                <span className="text-xs font-bold text-[#0C5B94] uppercase tracking-wider block mb-1">User Query</span>
                <p className="text-sm text-[#021426] font-medium">"What is the best local cafe?"</p>
              </div>
              <div className="w-0.5 h-8 bg-[#e5e9ee] -my-2 relative z-0"></div>
              <div className="bg-[#05192E] rounded-lg p-4 shadow-md w-full max-w-xs text-center relative z-10">
                <span className="text-xs font-bold text-[#FEAB3D] uppercase tracking-wider block mb-1">AI Engine</span>
                <p className="text-sm text-white font-medium">Synthesizing data sources...</p>
              </div>
              <div className="w-0.5 h-8 bg-[#e5e9ee] -my-2 relative z-0"></div>
              <div className="bg-white border border-[#FEAB3D] rounded-lg p-4 shadow-md w-full max-w-xs text-center relative z-10">
                <span className="text-xs font-bold text-[#FEAB3D] uppercase tracking-wider block mb-1">Recommendation</span>
                <p className="text-sm text-[#021426] font-bold">Your Business</p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. WHAT AI Search Advisers DOES */}
        <section className="py-20 lg:py-28 px-6 bg-[#FAF7F2]">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-16 max-w-2xl mx-auto">
              <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-6">
                See how AI sees your business.
              </h2>
              <p className="text-lg text-[#4C545B] leading-relaxed">
                We remove the guesswork from AI visibility. Our platform translates technical signals into clear, actionable intelligence.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Discover', desc: 'See whether AI systems can correctly understand and access your core business information.', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
                { title: 'Compare', desc: 'Understand how your AI visibility footprint compares with relevant competitors in your market.', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                { title: 'Diagnose', desc: 'Identify critical technical, content, citation or listing issues that prevent you from being recommended.', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
                { title: 'Improve', desc: 'Receive prioritized actions or hands-on implementation support depending on your selected plan.', icon: 'M13 10V3L4 14h7v7l9-11h-7z' }
              ].map((feature, idx) => (
                <div key={idx} className="bg-white border border-[#e5e9ee] p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-[#0C5B94]/10 rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-[#0C5B94]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={feature.icon} />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-[#021426] mb-3">{feature.title}</h3>
                  <p className="text-[#4C545B] text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. HOW IT WORKS */}
        <section className="py-20 lg:py-28 px-6 bg-[#05192E] text-white">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold mb-6">How it works</h2>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 relative">
              <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[1px] bg-white/20"></div>
              
              {[
                { step: '01', title: 'Enter your website', desc: 'Provide the primary domain you want AI Search Advisers to analyze.' },
                { step: '02', title: 'We run the analysis', desc: 'The platform reviews relevant visibility, technical and discovery signals.' },
                { step: '03', title: 'Get your report', desc: 'See your visibility findings and prioritized opportunities in one dashboard.' },
                { step: '04', title: 'Improve & monitor', desc: 'Use the recommendations yourself or choose a plan with ongoing support.' }
              ].map((item, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-[#FEAB3D] rounded-full flex items-center justify-center text-[#021426] font-bold text-xl mb-6 shadow-[0_0_20px_rgba(254,171,61,0.3)]">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed px-2">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. WHAT THE AUDIT CHECKS */}
        <section className="py-20 lg:py-28 px-6 bg-white border-b border-[#e5e9ee]">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-12 text-center">What the audit checks</h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
              {[
                { title: 'AI Visibility', desc: 'Baseline measurement of your brand\'s presence across key AI discovery platforms.' },
                { title: 'Technical Readiness', desc: 'Analysis of your site\'s architecture to ensure AI bots can efficiently parse content.' },
                { title: 'AI Crawler Access', desc: 'Verification that you are not accidentally blocking vital AI crawlers via robots.txt.' },
                { title: 'Structured Data', desc: 'Review of schema markup that helps AI confidently understand your business entities.' },
                { title: 'Competitor Visibility', desc: 'Snapshot of how your visibility compares to relevant competitors.' },
                { title: 'Website Health', desc: 'High-level check of performance and security factors that influence trust.' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded bg-[#FEAB3D]/20 flex items-center justify-center mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#FEAB3D]"></div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#021426] mb-2">{item.title}</h3>
                    <p className="text-[#4C545B] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. SHOW THE OUTPUT */}
        <section className="py-20 lg:py-28 px-6 bg-[#FAF7F2]">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-6">Know what to fix first.</h2>
            <p className="text-lg text-[#4C545B] mb-12 max-w-2xl mx-auto">
              Your audit generates a clear visibility scorecard, highlights high-priority technical issues, and provides recommended next actions without overwhelming you with raw data.
            </p>
            
            <div className="rounded-xl border border-[#e5e9ee] bg-white p-2 sm:p-4 shadow-xl mx-auto">
              <img 
                src="/learn-more-dashboard.png" 
                alt="AI Search Advisers Report Output" 
                className="w-full h-auto rounded-lg object-cover"
              />
            </div>
          </div>
        </section>

        {/* 7. FROM AUDIT TO ONGOING */}
        <section className="py-20 lg:py-28 px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-16">The path to better visibility</h2>
            
            <div className="flex flex-col md:flex-row justify-center items-stretch gap-6 mb-12">
              <div className="flex-1 bg-[#FAF7F2] p-8 rounded-xl border border-[#e5e9ee]">
                <h3 className="text-[#0C5B94] font-bold text-xl mb-3">1. Start</h3>
                <p className="text-[#021426] font-bold mb-2">Free Audit</p>
                <p className="text-[#4C545B] text-sm">Understand exactly where you stand today.</p>
              </div>
              <div className="flex-1 bg-[#FAF7F2] p-8 rounded-xl border border-[#e5e9ee]">
                <h3 className="text-[#0C5B94] font-bold text-xl mb-3">2. Monitor</h3>
                <p className="text-[#021426] font-bold mb-2">Pro Plan</p>
                <p className="text-[#4C545B] text-sm">Track your visibility and technical health over time.</p>
              </div>
              <div className="flex-1 bg-[#05192E] p-8 rounded-xl shadow-lg transform md:-translate-y-2">
                <h3 className="text-[#FEAB3D] font-bold text-xl mb-3">3. Improve</h3>
                <p className="text-white font-bold mb-2">Done For You</p>
                <p className="text-white/80 text-sm">Hands-on implementation and optimization by our team.</p>
              </div>
            </div>

            <button onClick={() => navigate('/pricing')} className="inline-flex px-8 py-3 bg-transparent border border-[#021426] text-[#021426] font-semibold rounded hover:bg-[#FAF7F2] transition-colors">
              Compare plans
            </button>
          </div>
        </section>

        {/* 8. WHY AI Search Advisers & 9. PRIVACY */}
        <section className="py-20 lg:py-28 px-6 bg-[#05192E] text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0C5B94] rounded-full blur-[120px] opacity-20 translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
          
          <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16 relative z-10">
            {/* Why Us */}
            <div>
              <h2 className="font-['Fraunces',serif] text-3xl font-bold mb-8">Why AI Search Advisers</h2>
              <ul className="space-y-6">
                <li className="flex gap-4">
                  <div className="w-6 h-6 shrink-0 mt-0.5 text-[#FEAB3D]">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Actionable recommendations</h3>
                    <p className="text-white/70 text-sm">We don't just provide raw monitoring data. We highlight exactly what needs to be fixed.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-6 h-6 shrink-0 mt-0.5 text-[#FEAB3D]">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Deep technical analysis</h3>
                    <p className="text-white/70 text-sm">We look under the hood at structured data and crawler access, not just keywords.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-6 h-6 shrink-0 mt-0.5 text-[#FEAB3D]">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Implementation support</h3>
                    <p className="text-white/70 text-sm">Choose hands-on help if you don't have the time or technical team to execute changes.</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Privacy */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 lg:p-10 backdrop-blur-sm">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </div>
              <h2 className="font-['Fraunces',serif] text-2xl font-bold mb-4">Built with privacy in mind.</h2>
              <p className="text-white/70 text-sm leading-relaxed mb-8">
                Our core analytics offering is cookieless by design. We focus on business visibility signals, not invasive personal tracking. 
              </p>
              <div className="flex flex-wrap gap-4 text-sm font-semibold">
                <Link to="/privacy-policy" className="text-white hover:text-[#FEAB3D] underline underline-offset-4 decoration-white/30 transition-colors">Read our Privacy Policy</Link>
                <Link to="/cookie-policy" className="text-white hover:text-[#FEAB3D] underline underline-offset-4 decoration-white/30 transition-colors">Cookie Policy</Link>
              </div>
            </div>
          </div>
        </section>

        {/* 10. WHO IT'S FOR */}
        <section className="py-20 lg:py-28 px-6 bg-white border-b border-[#e5e9ee]">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-12 text-center">Who relies on AI Search Advisers</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-8 border border-[#e5e9ee] rounded-xl hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-[#0C5B94] mb-3">Small Businesses</h3>
                <p className="text-[#4C545B] text-sm">Ensure local customers can find your services when asking AI assistants for recommendations nearby.</p>
              </div>
              <div className="p-8 border border-[#e5e9ee] rounded-xl hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-[#0C5B94] mb-3">Marketing Teams</h3>
                <p className="text-[#4C545B] text-sm">Monitor technical health and visibility trends to protect your brand's digital presence in a shifting landscape.</p>
              </div>
              <div className="p-8 border border-[#e5e9ee] rounded-xl hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-[#0C5B94] mb-3">Agencies</h3>
                <p className="text-[#4C545B] text-sm">Provide cutting-edge AI visibility audits and implementations as an added service for your client portfolio.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 11. FAQ */}
        <section className="py-20 lg:py-28 px-6 bg-[#FAF7F2]">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426] mb-12 text-center">Frequently asked questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-[#e5e9ee] bg-white rounded-lg overflow-hidden">
                  <button 
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-[#FAF7F2]/50 transition-colors"
                  >
                    <span className="font-bold text-[#021426] pr-4">{faq.q}</span>
                    <svg className={`w-5 h-5 text-[#0C5B94] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-[#4C545B] text-sm leading-relaxed border-t border-[#e5e9ee] pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 12. FINAL CTA */}
        <section className="py-24 px-6 bg-[#05192E] text-center relative overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-10 bg-[radial-gradient(circle_at_center,_#FEAB3D_0%,_transparent_60%)]"></div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="font-['Fraunces',serif] text-4xl sm:text-5xl font-bold text-white mb-6">See how AI sees your business.</h2>
            <p className="text-xl text-white/80 mb-10">Run your free audit today to identify critical visibility gaps. No credit card required.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/auth')} className="px-8 py-3.5 bg-[#FEAB3D] text-[#021426] font-semibold rounded hover:bg-[#FEAB3D]/90 transition-colors shadow-lg">
                Run My Free Audit
              </button>
              <button onClick={() => navigate('/pricing')} className="px-8 py-3.5 bg-transparent border border-white/20 text-white font-semibold rounded hover:bg-white/5 transition-colors">
                View Pricing
              </button>
            </div>
          </div>
        </section>

      </main>
      <GlobalFooter />
    </div>
  );
}
